/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: SecurityLogsPage.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Main page for Security Logs with list view and filters
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { Shield, RefreshCw, FilterX } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useSecurityLogList } from './hooks/useSecurityLogList';
import { SecurityLog } from './types/securityLog.types';
import { SecurityLogGrid } from './components/SecurityLogGrid';
import { MetadataModal } from '../auditLogs/components/MetadataModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';

export default function SecurityLogsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const [selectedLog, setSelectedLog] = useState<SecurityLog | null>(null);

  const pageTitle = useMemo(() => t('securityLogs.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
  } = useSecurityLogList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => (
              <span className="text-sm text-foreground">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : '—'}
              </span>
            )
          };
        }
        if (column.key === 'action') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => {
              const actionColors: Record<string, string> = {
                LOGIN: 'text-green-600 dark:text-green-400',
                LOGOUT: 'text-gray-600 dark:text-gray-400',
                LOGIN_FAILED: 'text-red-600 dark:text-red-400',
                PASSWORD_CHANGE: 'text-blue-600 dark:text-blue-400',
                PASSWORD_RESET: 'text-orange-600 dark:text-orange-400',
                MFA_ENABLED: 'text-purple-600 dark:text-purple-400',
                MFA_DISABLED: 'text-yellow-600 dark:text-yellow-400',
                PERMISSION_CHANGE: 'text-indigo-600 dark:text-indigo-400',
              };
              const colorClass = actionColors[item.action] || 'text-foreground';
              return (
                <span className={`text-sm font-medium ${colorClass}`}>
                  {item.action}
                </span>
              );
            }
          };
        }
        if (column.key === 'resource') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => (
              <span className="text-sm text-muted-foreground">
                {item.resource || '—'}
              </span>
            )
          };
        }
        if (column.key === 'user') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => (
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {item.user?.username || '—'}
                </span>
                {item.user?.fullName && (
                  <span className="text-xs text-muted-foreground">
                    {item.user.fullName}
                  </span>
                )}
              </div>
            )
          };
        }
        if (column.key === 'ipAddress') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => (
              <span className="font-mono text-sm text-muted-foreground">
                {item.ipAddress || '—'}
              </span>
            )
          };
        }
        if (column.key === 'metadata') {
          return {
            ...column,
            render: (_value: unknown, item: SecurityLog) => {
              const hasMetadata = item.metadata && Object.keys(item.metadata).length > 0;
              if (!hasMetadata) {
                return <span className="text-xs text-muted-foreground">—</span>;
              }
              return (
                <button
                  onClick={() => setSelectedLog(item)}
                  className="text-xs text-primary hover:underline"
                >
                  {t('securityLogs.viewDetails')}
                </button>
              );
            }
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.startDate !== '' ||
      filters.endDate !== '' ||
      (filters.actions.length > 0 && filters.actions.join(',') !== 'LOGIN,LOGOUT') ||
      filters.resource !== '' ||
      filters.userId !== '' ||
      filters.domainId !== ''
    );
  }, [filters]);

  return (
    <>
      <ModulePageContainer
        currentView="list"
        listView={
          <SecurityLogGrid
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onFilterChange={handleFilterChange}
          />
        }
        formView={null}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onReset={hasActiveFilters ? handleClearFilters : undefined}
        onSave={handleApplyFilters}
        resetText={
          <>
            <FilterX className="h-4 w-4 mr-2" />
            {t('securityLogs.clearFilters')}
          </>
        }
        saveText={
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('securityLogs.refresh')}
          </>
        }
        showCancel={false}
        showDelete={false}
        isSaving={loading}
      />

      {/* Metadata Modal */}
      {selectedLog && (
        <MetadataModal
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          metadata={selectedLog.metadata}
          action={selectedLog.action}
          resource={selectedLog.resource}
          timestamp={selectedLog.createdAt}
        />
      )}
    </>
  );
}

